import useLatest from 'react-scale/hooks/useLatest';
import { DefaultTokenConfig } from '../MarkdownEditorContext';

// Import remark specific libraries
import { remark } from 'remark';

import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import remarkLintEmphasisMarker from 'remark-lint-emphasis-marker';
import remarkLintStrongMarker from 'remark-lint-strong-marker';
import remarkLintListItemIndent from 'remark-lint-list-item-indent';
import remarkLintUnorderedListMarkerStyle from 'remark-lint-unordered-list-marker-style';
import remarkLintListContentIndent from 'remark-lint-list-item-content-indent';
import remarkLintBlockquoteIndentation from 'remark-lint-blockquote-indentation';
import remarkLintNoBlockquoteWithoutMarker from 'remark-lint-no-blockquote-without-marker';
import remarkLintListItemBulletIndent from 'remark-lint-list-item-bullet-indent';
import remarkLintNoEmptyUrl from 'remark-lint-no-empty-url';

import { BoldToken, ItalicToken, BulletListToken } from '@scale/shared/types/config';
import { useCallback } from 'react';

type UseMarkdownValidatorOptions = {
  enableMath?: boolean;
  defaultTokens?: DefaultTokenConfig;
};

function getItalicToken(italicToken?: ItalicToken) {
  switch (italicToken) {
    case ItalicToken.ASTERISK:
      return '*';
    case ItalicToken.UNDERSCORE:
      return '_';
    default:
      return '*';
  }
}

function getBoldToken(boldToken?: BoldToken) {
  switch (boldToken) {
    case BoldToken.ASTERISK:
      return '*';
    case BoldToken.UNDERSCORE:
      return '_';
    default:
      return '*';
  }
}

function getBulletListToken(bulletListToken?: BulletListToken) {
  switch (bulletListToken) {
    case BulletListToken.ASTERISK:
      return '*';
    case BulletListToken.DASH:
      return '-';
    case BulletListToken.PLUS:
      return '+';
    default:
      return '*';
  }
}

// NOTE: remark-math does not support custom delimiters, so we need to replace them with the standard ones
// In the future, we should use a custom plugin for this, since remark-math is officially won't support custom delimiters
const preprocessContent = (
  content: string,
  shouldProcessMathTokens?: boolean,
  shouldProcessNonMathTokens?: boolean,
) => {
  let processedContent = content;

  // Replace custom math delimiters with standard ones
  if (shouldProcessMathTokens) {
    processedContent
      .replace(/\\\(/g, '$') // Replace \( with $
      .replace(/\\\)/g, '$') // Replace \) with $
      .replace(/\\\[/g, '$$') // Replace \[ with $$
      .replace(/\\\]/g, '$$') // Replace \] with $$
      .replace(/\\\\\(/g, '$') // Replace \\( with $
      .replace(/\\\\\)/g, '$') // Replace \\) with $
      .replace(/\\\\\[/g, '$$') // Replace \\[ with $$
      .replace(/\\\\\]/g, '$$'); // Replace \\] with $$
  }

  // Replace custom bold, italics, and bullet list tokens with standard ones
  if (shouldProcessNonMathTokens) {
    processedContent = processedContent
      // Replace custom bold tokens (i.e `__`) with double asterisk tokens
      .replace(/(?:^|\s)(__)(?!\s+__)(?:[^_]+)(__)(?!\s+__)/g, (match, p1, p2) => {
        return match.replace(p1, BoldToken.ASTERISK).replace(p2, BoldToken.ASTERISK);
      })
      // Replace custom italic tokens (i.e `_`) with single asterisk tokens
      .replace(/(?:^|\s)(_)(?!\s+_)(?:[^_]+)(_)(?!\s+_)/g, (match, p1, p2) => {
        return match.replace(p1, ItalicToken.ASTERISK).replace(p2, ItalicToken.ASTERISK);
      })
      // Replace custom bullet list tokens (i.e `-`, `*`, `+`) with dash tokens
      .replace(/(^)([-+*])(?=\s)/gm, BulletListToken.DASH);
  }

  return processedContent;
};

function useMarkdownValidator(props: UseMarkdownValidatorOptions) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const enableMath = useLatest(props.enableMath ?? false);
  const defaultTokens = useLatest(props.defaultTokens);

  const validate = useCallback(
    async (content: string, shouldProcessNonMathTokens?: boolean) => {
      return validateMarkdown(content, {
        enableMath: enableMath.current,
        defaultTokens: defaultTokens.current,
        shouldProcessNonMathTokens,
      });
    },
    [enableMath, defaultTokens],
  );

  return {
    validate,
  };
}

type MarkdownValidatorOptions = {
  enableMath?: boolean;
  defaultTokens?: DefaultTokenConfig;
  shouldProcessNonMathTokens?: boolean;
};

// 12/12/2024: This function will eventually be copied over to a shared package. Once it is,
// we should use the version in the shared package instead of this one.
async function validateMarkdown(content: string, options?: MarkdownValidatorOptions) {
  const remarkInstance = remark();

  if (options?.enableMath) {
    remarkInstance.use(remarkMath, { singleDollarTextMath: true });
  }

  /**
   * If options.shouldProcessNonMathTokens is true, we will pre-process and replace bold, italic, and bullet list tokens
   * in the input content with their default tokens. This is done in conjunction with configuring remark to expect standard
   * bold, italic, and bullet list tokens to ensure that the validator does not flag these token inconsistencies as errors.
   */
  const preprocessedOriginalContent = preprocessContent(
    content,
    options?.enableMath,
    options?.shouldProcessNonMathTokens,
  );

  const defaultItalicToken = options?.shouldProcessNonMathTokens
    ? ItalicToken.ASTERISK
    : options?.defaultTokens?.italicToken;
  const defaultBoldToken = options?.shouldProcessNonMathTokens
    ? BoldToken.ASTERISK
    : options?.defaultTokens?.boldToken;
  const defaultBulletListToken = options?.shouldProcessNonMathTokens
    ? BulletListToken.DASH
    : options?.defaultTokens?.bulletListToken;

  const file = await remarkInstance
    .use(remarkGfm)
    .use(remarkLintEmphasisMarker, getItalicToken(defaultItalicToken))
    .use(remarkLintStrongMarker, getBoldToken(defaultBoldToken))
    .use(remarkLintListItemIndent, [2, 'space'])
    .use(remarkLintListItemBulletIndent, [2])
    .use(remarkLintListContentIndent, [2])
    .use(remarkLintBlockquoteIndentation)
    .use(remarkLintNoBlockquoteWithoutMarker)
    .use(remarkLintUnorderedListMarkerStyle, getBulletListToken(defaultBulletListToken))
    .use(remarkLintNoEmptyUrl)
    .use({
      settings: {
        emphasis: getItalicToken(defaultItalicToken),
        strong: getBoldToken(defaultBoldToken),
        bullet: getBulletListToken(defaultBulletListToken),
        listItemIndent: 'one',
        listItemContentIndent: 'one',
      },
    })
    .process(preprocessedOriginalContent);

  // eslint-disable-next-line no-console
  const fixedContent = String(file);
  return { fixedContent, preprocessedOriginalContent, messages: file.messages };
}

export default useMarkdownValidator;
